/**
 * Automatic table arrangement for group reservations.
 *
 * When a booking needs more seats than one table holds, its tables have to be
 * pushed together into a single block. Doing that by hand on a busy plan is
 * fiddly, and it is easy to end up blocking the route to the toilets or wedging
 * a table where no waiter can reach it.
 *
 * The rules this enforces, in the order they were asked for:
 *
 *   1. Tables move as little as possible. Every candidate arrangement is scored
 *      by the total distance its tables travel, and the cheapest valid one wins.
 *   2. Waiters can get round every table. Each side needs SERVICE_AISLE_CM of
 *      free space, except where the table is joined to another table in the same
 *      group, or where that side is against a wall — a table in a corner only
 *      needs its two open sides kept clear.
 *   3. Marked places stay reachable. Doors, toilets, stairs and the like must
 *      remain connected to each other by a corridor at least CORRIDOR_CM wide.
 *      This is checked properly, by eroding the free space and flood-filling it,
 *      not by guessing at straight lines.
 *   4. Pinned tables never move, and an arrangement builds around them.
 *
 * Everything here is pure and works in centimetres, so it can be tested without
 * a browser.
 */

// Free space a waiter needs down any side of a table.
export const SERVICE_AISLE_CM = 45;

// Minimum width of the route that has to survive between marked places.
export const CORRIDOR_CM = 80;

// Gap left between two tables pushed together. Small enough to read as one
// block, large enough that the tops are not fighting each other.
export const JOIN_GAP_CM = 2;

// A table this close to a wall counts as against it, and needs no aisle there.
export const WALL_TOUCH_CM = 12;

// Hard physical clearance from walls, fittings and other bookings' tables.
// Non-overlap alone allows a table to sit flush against a wall, which reads as
// a collision on the plan and is wrong in the room too: chairs need somewhere
// to go and skirting boards exist.
export const MIN_OBSTACLE_GAP_CM = 5;

// Resolution of the reachability grid. 10 cm keeps a 25x20 m room at
// 250x200 cells, which is quick to flood fill.
export const GRID_CM = 10;

// Element types a person has to be able to walk between. Everything else is
// furniture: it blocks space but nothing needs to reach it.
export const ACCESS_TYPES = new Set(["door", "wc", "stairs", "reception"]);

// Element types that do not obstruct anyone: a window is in the wall, and a
// plant is small enough to step around.
const NON_BLOCKING_TYPES = new Set(["window", "plant"]);

// The building envelope. These are the pieces that separate inside from
// outside, and they are what the usable interior is derived from. Doors count:
// a doorway is an opening in the envelope, not a hole in it, so leaving doors
// out would let the outside flood in through every entrance.
const SHELL_TYPES = new Set(["wall", "door", "window"]);

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const positive = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/** On-screen footprint of a box after rotation about its centre. */
export function footprint(width, height, rotation) {
  const radians = ((num(rotation) % 360) * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));

  return {
    width: width * cos + height * sin,
    height: width * sin + height * cos,
  };
}

/** Axis-aligned bounds of a placed item, accounting for rotation. */
export function boundsOf(item) {
  const width = positive(item.width, 80);
  const height = positive(item.height, 80);
  const extent = footprint(width, height, item.rotation);
  const centerX = num(item.left) + width / 2;
  const centerY = num(item.top) + height / 2;

  return {
    left: centerX - extent.width / 2,
    top: centerY - extent.height / 2,
    right: centerX + extent.width / 2,
    bottom: centerY + extent.height / 2,
    width: extent.width,
    height: extent.height,
    centerX,
    centerY,
  };
}

function overlaps(a, b, margin = 0) {
  return (
    a.left < b.right + margin &&
    b.left < a.right + margin &&
    a.top < b.bottom + margin &&
    b.top < a.bottom + margin
  );
}

/* ------------------------------------------------------------------ rule 2 */

/**
 * Can a waiter work at this table?
 *
 * Each of the four sides is one of three things:
 *
 *   joined  - shares an edge with another table in the same group. Internal to
 *             the block, so it is neither an approach nor an obstruction.
 *   open    - has a clear SERVICE_AISLE_CM strip to stand and serve in.
 *   closed  - a wall, a fixture, another table, or the room edge sits within
 *             that strip. Note this includes a gap too narrow to serve from:
 *             35 cm against a wall is not an aisle, it is a wall.
 *
 * The requirement is two open sides, so a waiter can genuinely get round the
 * table, dropping to one where something already closes a side. That is the
 * corner exemption: a table tucked against a wall, or into the angle of two,
 * only has to keep its remaining approach clear.
 *
 * Walls the user drew are ordinary blockers, so a room whose real walls sit
 * inside the canvas works the same as one that uses the canvas edge.
 */
export function hasServiceAccess(bounds, room, blockers, partners) {
  const sides = [
    {
      zone: {
        left: bounds.left - SERVICE_AISLE_CM,
        right: bounds.left,
        top: bounds.top,
        bottom: bounds.bottom,
      },
      atRoomEdge: bounds.left <= SERVICE_AISLE_CM,
    },
    {
      zone: {
        left: bounds.right,
        right: bounds.right + SERVICE_AISLE_CM,
        top: bounds.top,
        bottom: bounds.bottom,
      },
      atRoomEdge: bounds.right >= room.width - SERVICE_AISLE_CM,
    },
    {
      zone: {
        left: bounds.left,
        right: bounds.right,
        top: bounds.top - SERVICE_AISLE_CM,
        bottom: bounds.top,
      },
      atRoomEdge: bounds.top <= SERVICE_AISLE_CM,
    },
    {
      zone: {
        left: bounds.left,
        right: bounds.right,
        top: bounds.bottom,
        bottom: bounds.bottom + SERVICE_AISLE_CM,
      },
      atRoomEdge: bounds.bottom >= room.height - SERVICE_AISLE_CM,
    },
  ];

  let open = 0;
  let closed = 0;

  for (const side of sides) {
    // Strict overlap, with no tolerance. A tolerance here let a neighbour that
    // merely touches at the corner mark the two perpendicular sides as joined
    // too, so the middle table of a row stopped needing any aisle at all.
    if (partners.some((partner) => overlaps(side.zone, partner))) continue;

    if (side.atRoomEdge || blockers.some((b) => overlaps(side.zone, b))) {
      closed += 1;
    } else {
      open += 1;
    }
  }

  return open >= 2 || (open >= 1 && closed >= 1);
}

/* ------------------------------------------------------------------ rule 3 */

/**
 * Label which marked places can reach one another.
 *
 * The room is rasterised, obstacles are stamped in, and the free space is
 * eroded by half a corridor width so that only gaps a person actually fits
 * through survive. The surviving space is then split into connected regions and
 * each marked place is tagged with the region it sits in: two places can reach
 * each other exactly when they carry the same tag.
 *
 * Returns one integer per access point, or -1 where a place has no walkable
 * cell at all.
 */
/**
 * The floor a table is actually allowed to stand on.
 *
 * A room's declared width and height are the size of the drawing canvas, not
 * the size of the restaurant. Draw a 20 x 9 m shell on a 25 x 20 m canvas and
 * three hundred square metres of the canvas are not the room at all — but they
 * contain no walls and no fittings, so a placement out there passes every other
 * test and tables get arranged into the void.
 *
 * The interior is derived from the shell instead: rasterise it, then take every
 * free region that does not touch the edge of the canvas. Anything reachable
 * from the canvas edge is outside the building; anything enclosed is inside.
 *
 * Taking the union of the enclosed regions rather than the largest one matters
 * for real layouts: a room split by an internal partition, or an L-shape drawn
 * as two runs of wall, comes out as several regions and all of them are floor.
 *
 * Returns null when no shell has been drawn, or when the shell encloses
 * nothing — a half-drawn room then behaves as it did before, with the whole
 * canvas usable, rather than refusing every arrangement.
 */
export function interiorMask(room, elements) {
  const shell = (elements || []).filter((element) =>
    SHELL_TYPES.has(element.item_type),
  );
  if (!shell.length) return null;

  const cols = Math.max(1, Math.ceil(room.width / GRID_CM));
  const rows = Math.max(1, Math.ceil(room.height / GRID_CM));
  const blocked = new Uint8Array(cols * rows);

  // Rounded outwards, so two walls meeting at a corner always share a cell and
  // the envelope stays closed. Claiming only fully-covered cells looked tidier
  // but left a 10 cm hole where a wall ending at 216 cm met one starting at
  // 211 cm, the outside leaked in, and the whole room read as not-a-room.
  //
  // Over-claiming by up to a cell is corrected on the other side, in
  // withinInterior, which insets the box it is testing.
  shell.map(boundsOf).forEach((bounds) => {
    const x0 = Math.max(0, Math.floor(bounds.left / GRID_CM));
    const x1 = Math.min(cols - 1, Math.ceil(bounds.right / GRID_CM) - 1);
    const y0 = Math.max(0, Math.floor(bounds.top / GRID_CM));
    const y1 = Math.min(rows - 1, Math.ceil(bounds.bottom / GRID_CM) - 1);

    for (let y = y0; y <= y1; y += 1) {
      for (let x = x0; x <= x1; x += 1) blocked[y * cols + x] = 1;
    }
  });

  const inside = new Uint8Array(cols * rows);
  const seen = new Uint8Array(cols * rows);
  let enclosedCells = 0;

  for (let seed = 0; seed < inside.length; seed += 1) {
    if (blocked[seed] || seen[seed]) continue;

    const region = [];
    const queue = [seed];
    seen[seed] = 1;
    let touchesEdge = false;

    while (queue.length) {
      const cell = queue.pop();
      const x = cell % cols;
      const y = (cell - x) / cols;
      region.push(cell);

      if (x === 0 || y === 0 || x === cols - 1 || y === rows - 1) {
        touchesEdge = true;
      }

      const neighbours = [
        [x - 1, y],
        [x + 1, y],
        [x, y - 1],
        [x, y + 1],
      ];

      for (const [nx, ny] of neighbours) {
        if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue;
        const index = ny * cols + nx;
        if (blocked[index] || seen[index]) continue;
        seen[index] = 1;
        queue.push(index);
      }
    }

    if (touchesEdge) continue;

    region.forEach((cell) => {
      inside[cell] = 1;
    });
    enclosedCells += region.length;
  }

  if (!enclosedCells) return null;

  return { cols, rows, inside, cells: enclosedCells };
}

/**
 * Does this box lie wholly on the usable floor?
 *
 * The box is inset by one cell before sampling, to cancel the outward rounding
 * used when the envelope was rasterised. Without that, a table standing a
 * legitimate 13 cm clear of a wall was judged outside the room because the wall
 * and the table shared a grid cell.
 */
export function withinInterior(mask, bounds) {
  if (!mask) return true;

  const { cols, rows, inside } = mask;

  const left = bounds.left + GRID_CM;
  const right = bounds.right - GRID_CM;
  const top = bounds.top + GRID_CM;
  const bottom = bounds.bottom - GRID_CM;

  const centreCell = () => {
    const x = clamp(Math.floor(bounds.centerX / GRID_CM), 0, cols - 1);
    const y = clamp(Math.floor(bounds.centerY / GRID_CM), 0, rows - 1);
    return inside[y * cols + x] === 1;
  };

  // Anything a cell wide or less is judged by the cell its centre sits in.
  if (right <= left || bottom <= top) return centreCell();

  const x0 = Math.max(0, Math.floor(left / GRID_CM));
  const x1 = Math.min(cols - 1, Math.ceil(right / GRID_CM) - 1);
  const y0 = Math.max(0, Math.floor(top / GRID_CM));
  const y1 = Math.min(rows - 1, Math.ceil(bottom / GRID_CM) - 1);

  if (x1 < x0 || y1 < y0) return centreCell();

  for (let y = y0; y <= y1; y += 1) {
    for (let x = x0; x <= x1; x += 1) {
      if (inside[y * cols + x] !== 1) return false;
    }
  }

  return true;
}

export function accessComponents(room, obstacles, accessPoints) {
  const cols = Math.max(1, Math.ceil(room.width / GRID_CM));
  const rows = Math.max(1, Math.ceil(room.height / GRID_CM));
  const blocked = new Uint8Array(cols * rows);

  const stamp = (bounds, margin) => {
    const x0 = Math.max(0, Math.floor((bounds.left - margin) / GRID_CM));
    const x1 = Math.min(cols - 1, Math.ceil((bounds.right + margin) / GRID_CM) - 1);
    const y0 = Math.max(0, Math.floor((bounds.top - margin) / GRID_CM));
    const y1 = Math.min(rows - 1, Math.ceil((bounds.bottom + margin) / GRID_CM) - 1);

    for (let y = y0; y <= y1; y += 1) {
      for (let x = x0; x <= x1; x += 1) blocked[y * cols + x] = 1;
    }
  };

  // Erode by half a corridor so a surviving cell means a person fits.
  obstacles.forEach((obstacle) => stamp(obstacle, CORRIDOR_CM / 2));

  const openings = accessPoints.map((point) => ({
    x: clamp(Math.floor(point.centerX / GRID_CM), 0, cols - 1),
    y: clamp(Math.floor(point.centerY / GRID_CM), 0, rows - 1),
  }));

  // Carve the doorway back open.
  //
  // A door is drawn inside the wall it pierces, and a toilet against the wall
  // it opens off. Eroding that wall buries the opening, so without this even an
  // empty room reads as impassable. Clearing a corridor-radius disc reinstates
  // the passage through anything up to CORRIDOR_CM thick.
  const carve = Math.ceil(CORRIDOR_CM / GRID_CM);

  openings.forEach(({ x, y }) => {
    for (let dy = -carve; dy <= carve; dy += 1) {
      for (let dx = -carve; dx <= carve; dx += 1) {
        if (dx * dx + dy * dy > carve * carve) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && nx < cols && ny >= 0 && ny < rows) {
          blocked[ny * cols + nx] = 0;
        }
      }
    }
  });

  // Flood fill the free space, numbering each region.
  const labels = new Int32Array(cols * rows).fill(-1);
  let nextLabel = 0;

  for (let seed = 0; seed < labels.length; seed += 1) {
    if (blocked[seed] || labels[seed] !== -1) continue;

    const label = nextLabel;
    nextLabel += 1;
    const queue = [seed];
    labels[seed] = label;

    while (queue.length) {
      const cell = queue.pop();
      const x = cell % cols;
      const y = (cell - x) / cols;

      const neighbours = [
        [x - 1, y],
        [x + 1, y],
        [x, y - 1],
        [x, y + 1],
      ];

      for (const [nx, ny] of neighbours) {
        if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue;
        const index = ny * cols + nx;
        if (blocked[index] || labels[index] !== -1) continue;
        labels[index] = label;
        queue.push(index);
      }
    }
  }

  return openings.map(({ x, y }) => labels[y * cols + x]);
}

/** Every marked place reachable from every other. */
export function accessPointsConnected(room, obstacles, accessPoints) {
  if (accessPoints.length < 2) return true;

  const components = accessComponents(room, obstacles, accessPoints);
  return components.every(
    (label) => label !== -1 && label === components[0],
  );
}

/**
 * Does this layout keep every route the room already had?
 *
 * Absolute connectivity is the wrong test. A room can arrive with a route
 * already broken — a kitchen drawn across a service door, say — and demanding
 * perfection there makes the arranger refuse every booking for a fault it did
 * not cause and cannot fix. What matters is that it does not make things worse:
 * any pair of marked places that could reach each other before must still be
 * able to afterwards.
 */
export function preservesAccess(baseline, after) {
  for (let i = 0; i < baseline.length; i += 1) {
    for (let j = i + 1; j < baseline.length; j += 1) {
      const wasConnected =
        baseline[i] !== -1 && baseline[i] === baseline[j];
      if (!wasConnected) continue;

      const stillConnected = after[i] !== -1 && after[i] === after[j];
      if (!stillConnected) return false;
    }
  }

  return true;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/**
 * A design-time health check for a room.
 *
 * The arrangement rules are the same ones a room has to satisfy before service
 * starts, so they double as a critique of the layout itself. A room drawn too
 * tight does not fail loudly — it just quietly refuses group bookings during
 * service, which is the worst time to find out. This reports the problems while
 * the layout is still on screen and easy to change.
 *
 * @returns {{
 *   usableAreaM2: number|null, canvasAreaM2: number,
 *   seats: number, areaPerSeatM2: number|null,
 *   crowdedTables: Array<{id: *, name: *}>,
 *   outsideTables: Array<{id: *, name: *}>,
 *   collidingTables: Array<{id: *, name: *}>,
 *   unreachablePlaces: Array<string>,
 *   strayElements: Array<string>,
 * }}
 */
export function reviewRoom({ room, tables = [], elements = [] }) {
  const canvasAreaM2 = (room.width * room.height) / 10000;
  const interior = interiorMask(room, elements);
  const usableAreaM2 = interior
    ? (interior.cells * GRID_CM * GRID_CM) / 10000
    : null;

  const blockers = elements
    .filter(
      (element) =>
        !NON_BLOCKING_TYPES.has(element.item_type) &&
        !ACCESS_TYPES.has(element.item_type),
    )
    .map(boundsOf);

  const accessPoints = elements
    .filter((element) => ACCESS_TYPES.has(element.item_type))
    .map(boundsOf);

  const named = (table) => ({
    id: table.id,
    // Callers name tables differently: the editor uses table_name/table_number,
    // the plan's own export uses `number`. Fall through the lot so the label a
    // person sees is the one the report shows.
    name:
      table.table_name ??
      table.table_number ??
      table.number ??
      table.name ??
      table.id,
  });

  const bounds = tables.map((table) => ({ table, b: boundsOf(table) }));

  const crowdedTables = [];
  const outsideTables = [];
  const collidingTables = [];

  bounds.forEach(({ table, b }) => {
    if (
      b.left < -1 ||
      b.top < -1 ||
      b.right > room.width + 1 ||
      b.bottom > room.height + 1 ||
      !withinInterior(interior, b)
    ) {
      outsideTables.push(named(table));
    }

    if (blockers.some((blocker) => overlaps(b, blocker))) {
      collidingTables.push(named(table));
    }

    const others = bounds
      .filter((entry) => entry.table !== table)
      .map((entry) => entry.b);

    if (!hasServiceAccess(b, room, [...blockers, ...others], [])) {
      crowdedTables.push(named(table));
    }
  });

  // Marked places that cannot be reached from the rest of them, with the
  // tables where they are as the obstacles.
  const unreachablePlaces = [];
  if (accessPoints.length > 1) {
    const components = accessComponents(
      room,
      [...blockers, ...bounds.map((entry) => entry.b)],
      accessPoints,
    );
    const majority = components
      .slice()
      .sort(
        (a, b) =>
          components.filter((c) => c === b).length -
          components.filter((c) => c === a).length,
      )[0];

    elements
      .filter((element) => ACCESS_TYPES.has(element.item_type))
      .forEach((element, index) => {
        if (components[index] === -1 || components[index] !== majority) {
          unreachablePlaces.push(
            element.items_name || element.item_type,
          );
        }
      });
  }

  const strayElements = elements
    .map((element) => ({ element, b: boundsOf(element) }))
    .filter(
      ({ b }) =>
        b.left < -1 ||
        b.top < -1 ||
        b.right > room.width + 1 ||
        b.bottom > room.height + 1,
    )
    .map(({ element }) => element.items_name || element.item_type);

  const seats = tables.reduce(
    (total, table) => total + (Number(table.seats ?? table.number_of_seats) || 0),
    0,
  );

  return {
    canvasAreaM2,
    usableAreaM2,
    seats,
    areaPerSeatM2: seats && usableAreaM2 ? usableAreaM2 / seats : null,
    crowdedTables,
    outsideTables,
    collidingTables,
    unreachablePlaces,
    strayElements,
  };
}

/* ------------------------------------------------------------- arrangement */

/**
 * Lay the group out as a block, then slide the whole block around looking for
 * the cheapest position that satisfies every rule.
 *
 * @param {object} input
 * @param {Array}  input.groupTables  tables in the booking: {id, left, top, width, height, rotation, isLocked}
 * @param {Array}  input.otherTables  every other table in the room, same shape
 * @param {Array}  input.elements     room elements: {item_type, left, top, width, height, rotation}
 * @param {object} input.room         {width, height} in centimetres
 * @returns {{moves: Array, reason: string|null}}
 */
export function arrangeGroup({
  groupTables = [],
  otherTables = [],
  elements = [],
  room,
}) {
  if (!room || !(room.width > 0) || !(room.height > 0)) {
    return { moves: [], reason: "no-room" };
  }
  if (groupTables.length < 2) {
    return { moves: [], reason: "not-a-group" };
  }

  const movable = groupTables.filter((table) => !table.isLocked);
  if (!movable.length) {
    return { moves: [], reason: "all-locked" };
  }

  const blockingElements = elements
    .filter((element) => !NON_BLOCKING_TYPES.has(element.item_type))
    .map(boundsOf);

  const accessPoints = elements
    .filter((element) => ACCESS_TYPES.has(element.item_type))
    .map(boundsOf);

  // Obstacles for the reachability test exclude the marked places themselves.
  // A door is something you walk through and a toilet something you walk into;
  // stamping either as solid would wall off its own approach and make every
  // arrangement look impossible.
  const corridorElements = elements
    .filter(
      (element) =>
        !NON_BLOCKING_TYPES.has(element.item_type) &&
        !ACCESS_TYPES.has(element.item_type),
    )
    .map(boundsOf);

  const otherBounds = otherTables.map(boundsOf);
  const lockedBounds = groupTables.filter((t) => t.isLocked).map(boundsOf);

  // The floor the shell actually encloses. Null when no shell was drawn, in
  // which case the whole canvas stays usable as before.
  const interior = interiorMask(room, elements);

  // Which routes exist before the group is placed at all. Anything already
  // broken is not this arrangement's to fix, and must not block it either.
  const accessBaseline = accessComponents(
    room,
    [...corridorElements, ...otherBounds, ...lockedBounds],
    accessPoints,
  );

  // Everything that is not part of what we are placing.
  const staticBlockers = [...blockingElements, ...otherBounds];

  const sized = groupTables.map((table) => {
    const extent = footprint(
      positive(table.width, 80),
      positive(table.height, 80),
      table.rotation,
    );
    return { table, width: extent.width, height: extent.height };
  });

  // The block is built around a seed: a pinned table if the booking has one,
  // otherwise the table closest to the group's centre of gravity, which is the
  // choice that moves the others least.
  const pinned = sized.find((entry) => entry.table.isLocked);
  const centroid = averageCentre(groupTables);
  const seed =
    pinned ||
    sized.reduce((best, entry) => {
      const b = boundsOf(entry.table);
      const distance = Math.hypot(b.centerX - centroid.x, b.centerY - centroid.y);
      return !best || distance < best.distance ? { ...entry, distance } : best;
    }, null);

  // Pre-compute the block layouts once; only the shift changes per ring.
  const layouts = [];
  for (const shape of blockShapes(sized.length)) {
    for (const order of orderings(sized, seed)) {
      const layout = packBlock(order, shape);
      if (!layout) continue;

      const seedSlot = layout.slots.find(
        (slot) => slot.table.id === seed.table.id,
      );
      if (!seedSlot) continue;

      const seedBounds = boundsOf(seed.table);
      layouts.push({
        slots: layout.slots,
        baseX: seedBounds.left - seedSlot.dx,
        baseY: seedBounds.top - seedSlot.dy,
      });
    }
  }

  const candidates = [];

  // A pinned seed cannot move, so only the in-place position is meaningful.
  const radii = pinned ? [0] : SHIFT_RADII_CM;

  for (const radius of radii) {
    for (const [sx, sy] of shiftRing(radius)) {
      for (const layout of layouts) {
        const placements = layout.slots.map((slot) => ({
          table: slot.table,
          left: layout.baseX + slot.dx + sx,
          top: layout.baseY + slot.dy + sy,
          width: slot.width,
          height: slot.height,
        }));

        const candidate = evaluate({
          placements,
          room,
          staticBlockers,
          lockedBounds,
          accessPoints,
          accessBaseline,
          corridorElements,
          otherBounds,
          interior,
        });

        if (candidate) candidates.push(candidate);
      }
    }

    // Stop at the first ring that works: anything further out costs more travel.
    if (candidates.length) break;
  }

  if (!candidates.length) {
    // More than one pinned table in a booking can only be joined if they were
    // already touching, since neither may move. Say so, rather than blaming
    // space the room may well have.
    const pinnedCount = groupTables.filter((t) => t.isLocked).length;
    return {
      moves: [],
      reason: pinnedCount > 1 ? "pinned-tables-apart" : "no-valid-arrangement",
      // Useful when diagnosing a room: how much floor the shell encloses, and
      // whether confinement was in force at all.
      interiorCells: interior ? interior.cells : null,
    };
  }

  candidates.sort((a, b) => a.cost - b.cost);
  const best = candidates[0];

  // Report only tables that actually need to move, so the caller does not
  // rewrite rows for no reason.
  const moves = best.placements
    .filter((placement) => {
      const current = boundsOf(placement.table);
      return (
        Math.abs(current.left - placement.left) > 0.5 ||
        Math.abs(current.top - placement.top) > 0.5
      );
    })
    .map((placement) => {
      const table = placement.table;
      const extent = footprint(
        positive(table.width, 80),
        positive(table.height, 80),
        table.rotation,
      );
      // left/top address the unrotated box, while the layout works in
      // footprints, so convert back through the shared centre.
      const centerX = placement.left + extent.width / 2;
      const centerY = placement.top + extent.height / 2;

      return {
        id: table.id,
        left: round2(centerX - positive(table.width, 80) / 2),
        top: round2(centerY - positive(table.height, 80) / 2),
      };
    });

  return { moves, reason: null, cost: round2(best.cost) };
}

// The block is tried where it already sits first, then on widening rings around
// that. Nearer rings are searched first and the search stops at the first ring
// that yields anything valid, so rule 1 still holds while a group in a congested
// corner can reach open floor several metres away instead of being refused.
const SHIFT_RADII_CM = [0, 40, 80, 120, 180, 240, 320, 420, 540];
const SHIFT_STEP_CM = 40;

function shiftRing(radius) {
  if (radius === 0) return [[0, 0]];

  const offsets = [];
  for (let value = -radius; value <= radius; value += SHIFT_STEP_CM) {
    // Top and bottom edges of the ring.
    offsets.push([value, -radius], [value, radius]);
    // Left and right edges, corners already covered above.
    if (value > -radius && value < radius) {
      offsets.push([-radius, value], [radius, value]);
    }
  }
  return offsets;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

function averageCentre(tables) {
  const sum = tables.reduce(
    (acc, table) => {
      const b = boundsOf(table);
      return { x: acc.x + b.centerX, y: acc.y + b.centerY };
    },
    { x: 0, y: 0 },
  );

  return { x: sum.x / tables.length, y: sum.y / tables.length };
}

/** Grid shapes worth trying for n tables: a row, a column, and squarer blocks. */
function blockShapes(count) {
  const shapes = new Set([`1x${count}`, `${count}x1`]);

  for (let cols = 2; cols < count; cols += 1) {
    if (count % cols === 0) shapes.add(`${cols}x${count / cols}`);
  }

  return Array.from(shapes).map((key) => {
    const [cols, rows] = key.split("x").map(Number);
    return { cols, rows };
  });
}

/** The seed first, then the rest — plus the reverse, which often fits better. */
function orderings(sized, seed) {
  const rest = sized.filter((entry) => entry.table.id !== seed.table.id);
  return [
    [seed, ...rest],
    [seed, ...rest.slice().reverse()],
  ];
}

/**
 * Lay the group out on a grid.
 *
 * Columns are as wide as the widest table in them and rows as tall as the
 * tallest, and positions accumulate from those actual sizes rather than from one
 * cell size for the whole block. That matters for the common case of joining
 * tables of different sizes: sizing every cell to the largest table and centring
 * within it left a 120 cm table floating 40 cm clear of its 200 cm neighbour
 * instead of touching it.
 *
 * A single row or column therefore snaps exactly, whatever the mix of sizes. In
 * a genuine two-dimensional block a smaller table can still sit slightly off its
 * neighbour, which is unavoidable without rotating tables.
 */
function packBlock(order, shape) {
  if (shape.cols * shape.rows < order.length) return null;

  const cellOf = (index) => ({
    col: index % shape.cols,
    row: Math.floor(index / shape.cols),
  });

  const columnWidths = new Array(shape.cols).fill(0);
  const rowHeights = new Array(shape.rows).fill(0);

  order.forEach((entry, index) => {
    const { col, row } = cellOf(index);
    columnWidths[col] = Math.max(columnWidths[col], entry.width);
    rowHeights[row] = Math.max(rowHeights[row], entry.height);
  });

  const columnOffsets = [];
  let x = 0;
  for (let col = 0; col < shape.cols; col += 1) {
    columnOffsets[col] = x;
    x += columnWidths[col] + JOIN_GAP_CM;
  }

  const rowOffsets = [];
  let y = 0;
  for (let row = 0; row < shape.rows; row += 1) {
    rowOffsets[row] = y;
    y += rowHeights[row] + JOIN_GAP_CM;
  }

  /**
   * Where a table sits inside its cell when the cell is bigger than it is.
   *
   * Centring looked neutral but left a gap: a 100 cm table centred in a column
   * sized for a 200 cm neighbour floats 50 cm clear of the table beside it, so
   * the block is not actually joined. Pushing each table toward the middle of
   * the block instead keeps the seams tight and moves the slack to the outside
   * edges, where nothing needs to touch.
   */
  const offsetInCell = (cellStart, cellSize, itemSize, index, count) => {
    const slack = cellSize - itemSize;
    if (slack <= 0 || count < 2) return cellStart + slack / 2;

    const middle = (count - 1) / 2;
    if (index < middle) return cellStart + slack; // push right/down, toward the middle
    if (index > middle) return cellStart;         // push left/up, toward the middle
    return cellStart + slack / 2;                 // dead centre of an odd run
  };

  const slots = order.map((entry, index) => {
    const { col, row } = cellOf(index);

    return {
      table: entry.table,
      width: entry.width,
      height: entry.height,
      dx: offsetInCell(
        columnOffsets[col],
        columnWidths[col],
        entry.width,
        col,
        shape.cols,
      ),
      dy: offsetInCell(
        rowOffsets[row],
        rowHeights[row],
        entry.height,
        row,
        shape.rows,
      ),
    };
  });

  return { slots };
}

/** Check one candidate against every rule; return it scored, or null. */
function evaluate({
  placements,
  room,
  staticBlockers,
  lockedBounds,
  accessPoints,
  accessBaseline,
  corridorElements,
  otherBounds,
  interior,
}) {
  const placedBounds = placements.map((placement) => ({
    left: placement.left,
    top: placement.top,
    right: placement.left + placement.width,
    bottom: placement.top + placement.height,
    centerX: placement.left + placement.width / 2,
    centerY: placement.top + placement.height / 2,
  }));

  // Inside the room, and on floor the shell actually encloses. The canvas
  // bounds alone are not enough: a shell drawn smaller than the canvas leaves
  // space that passes every other test but is not part of the restaurant.
  for (const bounds of placedBounds) {
    if (
      bounds.left < 0 ||
      bounds.top < 0 ||
      bounds.right > room.width ||
      bounds.bottom > room.height
    ) {
      return null;
    }

    if (!withinInterior(interior, bounds)) return null;
  }

  // A pinned table must end up exactly where it already is.
  for (let i = 0; i < placements.length; i += 1) {
    if (!placements[i].table.isLocked) continue;
    const current = boundsOf(placements[i].table);
    if (
      Math.abs(current.left - placements[i].left) > 0.5 ||
      Math.abs(current.top - placements[i].top) > 0.5
    ) {
      return null;
    }
  }

  // Nothing may touch a wall, a fitting, or another booking's table: a real
  // gap is required, not merely the absence of overlap.
  for (let i = 0; i < placedBounds.length; i += 1) {
    for (const blocker of staticBlockers) {
      if (overlaps(placedBounds[i], blocker, MIN_OBSTACLE_GAP_CM)) return null;
    }
    // Tables in the same group are meant to touch, so they only may not
    // overlap each other.
    for (let j = i + 1; j < placedBounds.length; j += 1) {
      if (overlaps(placedBounds[i], placedBounds[j], -1)) return null;
    }
  }

  // Rule 2: a waiter can reach every table.
  for (let i = 0; i < placedBounds.length; i += 1) {
    const partners = placedBounds.filter((_, index) => index !== i);
    const blockers = [...staticBlockers, ...lockedBounds];

    if (!hasServiceAccess(placedBounds[i], room, blockers, partners)) {
      return null;
    }
  }

  // Rule 3: no route that existed beforehand may be severed.
  const obstacles = [
    ...corridorElements,
    ...otherBounds,
    ...lockedBounds,
    ...placedBounds,
  ];

  const after = accessComponents(room, obstacles, accessPoints);
  if (!preservesAccess(accessBaseline, after)) return null;

  // Rule 1: cheapest total travel wins.
  const cost = placements.reduce((total, placement) => {
    const current = boundsOf(placement.table);
    return (
      total +
      Math.hypot(current.left - placement.left, current.top - placement.top)
    );
  }, 0);

  return { placements, cost };
}

export default arrangeGroup;
